/* ==========================================================================
   hh-toc.js — builds the sticky "on this page" rail            run ㍹

   WHY THIS IS JAVASCRIPT AND NOT LIQUID. The policy pages' table of contents
   lives inside `page.content` — store-level content on seven PUBLISHED pages,
   which this repo is forbidden to edit (CLAUDE.md hard guardrail). It also
   arrives as a Word/Termly export: a flat run of `div.MsoNormal` siblings,
   some carrying two anchors to the same target, several carrying an anchor and
   no text at all. There is no Liquid seam to render a rail from and no server
   side to clean it up, so the rail is assembled from what is actually in the
   DOM and the source block is hidden in place.

   TWO SOURCES, ONE COMPONENT — this is the part that makes it reusable on
   Recipes and Blog, which have no in-body ToC to adopt:
     `anchors`  harvest an existing in-body ToC (the policy pages)
     `headings` generate from the prose's own headings, adding ids where a
                heading has none (Recipes, Blog)
     `auto`     adopt if a parseable in-body ToC is found, else generate
   Nothing in this file knows what a policy page is.

   FAILURE IS ALWAYS "LEAVE THE PAGE ALONE". If the harvest finds fewer than
   data-min-items entries the rail stays hidden and the source ToC is never
   marked, so the page renders exactly as it does without this script.
   ========================================================================== */

(() => {
  'use strict';

  const TOC_LABEL = /^\s*table of contents\s*$/i;

  /* Anchor targets in a Word export are often <span id> INSIDE a heading, and
     some are legacy <a name>. Resolve both, and never trust a ToC entry whose
     target is not actually on the page — a dead rail link is worse than a
     missing one. */
  const resolveTarget = (hash) => {
    const id = hash.slice(1);
    if (!id) return null;
    return document.getElementById(id) || document.querySelector(`[name="${CSS.escape(id)}"]`);
  };

  /* ------------------------------------------------------------------------
     SOURCE 1 — adopt an existing in-body table of contents.

     Region detection: start at the element whose entire text is "Table of
     contents", climb to the block that is a child of the prose's own list of
     siblings, then walk forward. A sibling belongs to the ToC while it either
     carries a same-page anchor OR is empty (the export is full of empty
     spacer divs). The first sibling with visible text and NO anchor is the
     start of the document body, and the walk stops there — verified against
     terms-and-conditions, where 62 of the page's 64 same-page anchors sit
     inside this region and the walk stops on "TERMS OF USE".
     ------------------------------------------------------------------------ */
  const harvestFromLabel = (scope, label) => {
    let head = label;
    while (head.parentElement && head.parentElement !== scope && head.parentElement.children.length < 2) {
      head = head.parentElement;
    }
    const parent = head.parentElement;
    if (!parent) return null;

    const siblings = Array.from(parent.children);
    let i = siblings.indexOf(head);
    if (i < 0) {
      head = label.closest('div');
      i = siblings.indexOf(head);
      if (i < 0) return null;
    }

    const region = [head];
    for (let n = i + 1; n < siblings.length; n += 1) {
      const el = siblings[n];
      const hasText = el.textContent.trim().length > 0;
      const hasAnchor = el.querySelector('a[href^="#"]');
      if (hasText && !hasAnchor) break;
      region.push(el);
    }

    /* ONE ENTRY = ONE LINE, AND THE LABEL COMES FROM THE LINE, NOT THE LINK.
       Read straight off the anchors and the rail comes out wrong in two
       different ways, both real in this export:

         "6. CONTRIBUTION LICENSE"  the <a> wraps only "6. CONTRIBUTION " and
                                    leaves "LICENSE" outside it as a bare text
                                    node, so the anchor's own text is a
                                    truncation of the entry
         "5. USER GENERATED …"      the entry carries a SECOND, text-less
                                    anchor pointing at the NEXT section
                                    (#license), so "first anchor wins" would
                                    file this line under the wrong target

       So each sibling in the region is treated as one line: the label is the
       line's full text, and the destination is the first anchor on it that
       actually has text — the text-less ones are export debris. Lines with no
       texted anchor, and repeats of a target already claimed by an earlier
       line, are dropped. */
    const byHref = new Map();
    region.slice(1).forEach((el) => {
      const label = el.textContent.trim().replace(/\s+/g, ' ');
      if (!label) return;

      let href = null;
      let target = null;
      for (const a of el.querySelectorAll('a[href^="#"]')) {
        if (!a.textContent.trim()) continue;
        const candidate = a.getAttribute('href');
        if (!candidate || candidate === '#') continue;
        const resolved = resolveTarget(candidate);
        if (!resolved) continue;
        href = candidate;
        target = resolved;
        break;
      }
      if (!href || byHref.has(href)) return;

      byHref.set(href, { href, text: label, target, level: 1 });
    });

    return { items: Array.from(byHref.values()), region };
  };

  /* EVERY "TABLE OF CONTENTS" ON THE PAGE IS A CANDIDATE, AND THE FIRST ONE IS
     USUALLY NOT THE RIGHT ONE. privacy-policy says "…or by using our <a><em>table
     of contents</em></a> below to find the section you are looking for" in its
     summary, which matches the label exactly and comes FIRST in document order;
     its real ToC heading is 100-odd siblings further down. Trusting the first
     match harvested a region of one prose paragraph, produced nothing, and left
     that page with no rail at all — which is how this was found.

     So each candidate is tried in turn and the first region that yields a
     usable number of entries wins. That also means the min-items floor is a
     harvest CRITERION, not just a final gate: a candidate producing two
     entries is a mis-detection to move past, not a two-item rail. */
  const harvestAnchors = (scope, minItems) => {
    const candidates = Array.from(scope.querySelectorAll('*')).filter(
      (el) => !el.children.length && TOC_LABEL.test(el.textContent)
    );
    for (const label of candidates) {
      const found = harvestFromLabel(scope, label);
      if (found && found.items.length >= minItems) return found;
    }
    return null;
  };

  /* ------------------------------------------------------------------------
     SOURCE 2 — generate from the prose's own headings.

     Serves two different bodies with one code path:
       Recipes and Blog   real <h2>/<h3> in an article body
       the policy pages   Termly's `div[data-custom-class="heading_1"]`, which
                          is a HEADING IN NAME ONLY — a div, so it carries no
                          level in its tag name, it nests inside another
                          identical div often enough to double every entry, and
                          it repeats the same text verbatim (shipping-policy
                          lists "WHAT ARE MY SHIPPING & DELIVERY OPTIONS?"
                          twice). All three have to be handled here or those
                          pages get a ToC full of duplicates.
     ------------------------------------------------------------------------ */

  /* A real heading carries its level in its tag; a Termly div carries it in
     data-custom-class. Anything else counts as top level rather than being
     dropped — a rail with a flat hierarchy still works, one with missing
     entries does not. */
  const levelOf = (el) => {
    const tag = /^H([1-6])$/.exec(el.tagName);
    if (tag) return Number(tag[1]);
    const dcc = /heading_([1-6])/.exec(el.getAttribute('data-custom-class') || '');
    if (dcc) return Number(dcc[1]);
    return 1;
  };

  const harvestHeadings = (scope, selector) => {
    let nodes = Array.from(scope.querySelectorAll(selector)).filter(
      (el) => el.textContent.trim().length > 0
    );

    /* Drop a candidate that CONTAINS another candidate: the export wraps a
       heading div around an identical inner one, and keeping the outer would
       list every heading on the page twice. The inner is the tighter target to
       scroll to, so the outer is the one to lose. */
    nodes = nodes.filter((el) => !nodes.some((other) => other !== el && el.contains(other)));

    /* Then drop verbatim repeats of a label already taken — same reason, but
       for siblings rather than nesting. First occurrence wins: it is the one
       the reader reaches first. */
    const seenText = new Set();
    nodes = nodes.filter((el) => {
      const key = el.textContent.trim().replace(/\s+/g, ' ').toLowerCase();
      if (seenText.has(key)) return false;
      seenText.add(key);
      return true;
    });

    if (!nodes.length) return null;

    const used = new Set();
    const topLevel = Math.min(...nodes.map(levelOf));

    const items = nodes.map((el) => {
      if (!el.id) {
        const base =
          el.textContent
            .trim()
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .slice(0, 60) || 'section';
        let id = base;
        let n = 2;
        while (used.has(id) || document.getElementById(id)) {
          id = `${base}-${n}`;
          n += 1;
        }
        el.id = id;
      }
      used.add(el.id);
      return {
        href: `#${el.id}`,
        text: el.textContent.trim().replace(/\s+/g, ' '),
        target: el,
        level: levelOf(el) - topLevel + 1,
      };
    });

    return { items, region: null };
  };

  /* ------------------------------------------------------------------------
     Scroll-spy. Deliberately a scroll read rather than an IntersectionObserver:
     the question is not "which heading is visible" (several are, and a long
     section fills the viewport with none of them in it) but "which section am
     I inside" — the last target whose top has passed the sticky offset. One
     rAF-throttled pass over an array of already-resolved elements.
     ------------------------------------------------------------------------ */
  const spy = (nav, items) => {
    const links = items.map((item) => nav.querySelector(`a[href="${CSS.escape(item.href)}"]`));
    let active = -1;
    let queued = false;

    const offset = () => {
      const styles = getComputedStyle(document.documentElement);
      const sticky = parseFloat(styles.getPropertyValue('--sticky-header-margin-top')) || 0;
      return sticky + 24;
    };

    const read = () => {
      queued = false;
      const limit = offset() + 8;
      let next = 0;
      for (let i = 0; i < items.length; i += 1) {
        if (items[i].target.getBoundingClientRect().top <= limit) next = i;
        else break;
      }
      /* At the very bottom the last section may never reach the offset, so the
         final entry wins once the page is scrolled to the end. */
      const scroller = document.scrollingElement || document.documentElement;
      if (scroller.scrollTop + window.innerHeight >= scroller.scrollHeight - 4) {
        next = items.length - 1;
      }
      if (next === active) return;
      if (links[active]) links[active].removeAttribute('aria-current');
      active = next;
      const link = links[active];
      if (!link) return;
      link.setAttribute('aria-current', 'true');
      /* Keep the active entry inside a rail that is taller than the viewport,
         without ever scrolling the PAGE — scrollIntoView on a sticky child
         does exactly that, so the nav's own scrollTop is nudged instead. */
      const navBox = nav.getBoundingClientRect();
      const linkBox = link.getBoundingClientRect();
      if (linkBox.top < navBox.top) {
        nav.scrollTop -= navBox.top - linkBox.top + 8;
      } else if (linkBox.bottom > navBox.bottom) {
        nav.scrollTop += linkBox.bottom - navBox.bottom + 8;
      }
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(read);
    };

    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll, { passive: true });
    read();
  };

  const build = (nav) => {
    /* `querySelector('')` THROWS rather than returning null, so an empty or
       missing selector has to be caught before the call, not after it. */
    if (!nav.dataset.scope) return;
    const scope = document.querySelector(nav.dataset.scope);
    if (!scope) return;

    const source = nav.dataset.source || 'auto';
    const minItems = Number(nav.dataset.minItems) || 3;
    let harvested = null;

    if (source === 'anchors' || source === 'auto') harvested = harvestAnchors(scope, minItems);
    if (!harvested && (source === 'headings' || source === 'auto')) {
      harvested = harvestHeadings(scope, nav.dataset.headings || 'h2, h3');
    }
    if (!harvested || harvested.items.length < minItems) return;

    const list = nav.querySelector('.hh-toc__list');
    if (!list) return;

    const frag = document.createDocumentFragment();
    harvested.items.forEach((item) => {
      const li = document.createElement('li');
      li.className = `hh-toc__item${item.level > 1 ? ' hh-toc__item--sub' : ''}`;
      const a = document.createElement('a');
      a.className = 'hh-toc__link';
      a.href = item.href;
      a.textContent = item.text;
      li.appendChild(a);
      frag.appendChild(li);
    });
    list.replaceChildren(frag);

    /* Marking the source is the LAST thing that happens, and only on a
       harvest that produced a rail: the CSS that hides it keys off this class,
       so a page that got no rail can never end up with no ToC either. */
    if (harvested.region) {
      harvested.region.forEach((el) => el.setAttribute('data-hh-toc-source', ''));
      scope.classList.add('hh-toc-adopted');
    }

    /* SUPERSEDE (articles). Unlike the policy pages, whose in-body list is
       inside the prose and gets marked as a source above, an article's
       existing table of contents is a SEPARATE SECTION stacked above the body
       — hh-article-toc. It cannot be marked as a harvest source because it is
       not in the scope, and it must not simply be deleted, because it is the
       only ToC below 1024 where the rail does not render. So the rail names it
       and the CSS hides it inside the same >=1024 query the rail lives in.

       Set only once a rail exists, for the same reason the source marking is:
       a page that got no rail must not lose the ToC it already had. */
    if (nav.dataset.supersedes) {
      document
        .querySelectorAll(nav.dataset.supersedes)
        .forEach((el) => el.setAttribute('data-hh-toc-superseded', ''));
    }

    nav.hidden = false;
    spy(nav, harvested.items);
  };

  /* THE HOOK IS `data-hh-toc-rail`, NOT `data-hh-toc`. sections/hh-article-toc
     already owns the plain `data-hh-toc` attribute on its own root, and this
     script matching it was a real, silent failure on the article templates:
     init() reached the article band first, that element has no data-scope, the
     empty selector threw, and NodeList.forEach propagates — so the exception
     killed the loop before the actual rail was ever built. The rail simply did
     not appear, with nothing in the markup to suggest why.

     Hence both halves of the fix: a hook nothing else answers to, AND a
     try/catch so one bad node can never take the others down with it. */
  const init = () =>
    document.querySelectorAll('[data-hh-toc-rail]').forEach((nav) => {
      try {
        build(nav);
      } catch (err) {
        /* A rail that fails to build must leave the page exactly as it found
           it — which, below, it has: the source marking and the supersede
           marking are both the last things build() does. */
        console.warn('hh-toc: skipped a rail', err);
      }
    });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
